import type { Job, JobProgress } from "@/lib/jobs";

import { jobsSummary, mobileStatus, percent } from "./schedule";

/**
 * The jobs card on a phone — `6j`. Between Decks and Tags.
 *
 * One line of status where the desktop spreads three columns, and a 44px action control. Tapping
 * the row opens the detail; the action control does not, so Run is one tap from the list.
 */
export function JobsCardMobile({
  jobs,
  live,
  onOpen,
  onRun,
  onRetry,
}: {
  jobs: Job[];
  live: JobProgress | null;
  onOpen: (id: string) => void;
  onRun: (id: string) => void;
  onRetry: (id: string) => void;
}) {
  return (
    <section className="overflow-hidden rounded-[var(--r-3)] border border-line bg-panel">
      <div className="flex items-center gap-[9px] border-b border-line-soft px-3 py-[9px]">
        <span className="text-field-label font-semibold uppercase tracking-[.1em] text-ink-3">
          ジョブ
        </span>
        <span className="text-meta text-ink-4">{jobsSummary(jobs)}</span>
      </div>

      {jobs.map((job) => {
        const running = job.state === "running";
        const queued = job.state === "queued";
        const failed = job.state === "failed";
        const mine = live?.jobId === job.id ? live : null;
        return (
          <div
            key={job.id}
            className={[
              "flex min-h-[56px] items-center gap-[11px] border-b border-line-soft px-3 py-2 last:border-b-0",
              failed ? "bg-accent-ground" : running ? "bg-panel" : "",
            ].join(" ")}
          >
            <span
              className={[
                "size-2 shrink-0 rounded-full",
                running
                  ? "bg-accent"
                  : queued
                    ? "border-[1.5px] border-accent-line"
                    : failed
                      ? "bg-accent"
                      : job.state === "passed"
                        ? "bg-ink-3"
                        : job.lastRun
                          ? "bg-ink-off"
                          : "border border-line-strong",
              ].join(" ")}
            />
            <button
              type="button"
              onClick={() => onOpen(job.id)}
              data-check={`setup.job.${job.id}`}
              className="flex min-w-0 flex-1 flex-col gap-[3px] text-left"
            >
              <span className="text-[15px]/[1.3] font-semibold text-ink">{job.name}</span>
              <span
                className={[
                  "truncate text-meta",
                  running
                    ? "text-accent-text"
                    : failed || job.blocked
                      ? "text-accent-text"
                      : "text-ink-3",
                ].join(" ")}
              >
                {mobileStatus(job, mine)}
              </span>
            </button>

            {running ? (
              // Not a button: while a job runs, the only action is Cancel, and that lives on the
              // detail screen where the reassurance line sits next to it.
              <span className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-[var(--r-3)] bg-accent-tint px-2 font-mono text-[14px]/[1] text-accent-text">
                {percent(mine?.current ?? 0, mine?.total ?? 0)}%
              </span>
            ) : queued ? (
              // Same reasoning as the running chip: dropping a pending run is a Cancel, and Cancel
              // lives on the detail screen. A `Run` button here would offer to queue a job that is
              // already queued.
              <span className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-[var(--r-3)] border border-accent-line px-2 font-mono text-[14px]/[1] text-accent-text">
                {job.queuePosition ?? "—"}
              </span>
            ) : failed && job.retryable ? (
              <button
                type="button"
                onClick={() => onRetry(job.id)}
                className="min-h-[44px] min-w-[44px] rounded-[var(--r-3)] bg-accent px-3 text-[14px]/[1] font-semibold text-white"
              >
                再試行
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onRun(job.id)}
                className="min-h-[44px] min-w-[44px] rounded-[var(--r-3)] border border-line-strong bg-panel px-3 text-[14px]/[1] font-semibold text-ink-2"
              >
                実行
              </button>
            )}

            <button
              type="button"
              onClick={() => onOpen(job.id)}
              aria-label={`${job.name} を開く`}
              className="shrink-0 text-[16px]/[1] text-ink-4"
            >
              ›
            </button>
          </div>
        );
      })}
    </section>
  );
}
